'use client';

import { ChevronRight, ExternalLink } from 'lucide-react';
import type { CardTheme } from '@/types/serve-finder';

interface CardCTAProps {
  /** CTA label text */
  label: string;
  /** Icon to show after label (default: ChevronRight) */
  icon?: 'chevron' | 'external';
  /** Custom icon component override */
  customIcon?: React.ReactNode;
  /** Optional theme for accent/border colors */
  theme?: CardTheme | null;
  /** Accent color override (used when no theme) */
  accentColor?: string;
  /** Border color override */
  borderColor?: string;
  /** Whether this is an unthemed card (uses CSS variables) */
  useDefaultColors?: boolean;
  /** Optional click handler (for buttons inside cards that open different things) */
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

/**
 * Shared CTA footer for serve-finder cards.
 * Renders a border-top bar with centered label + icon, themed colors.
 */
export function CardCTA({
  label,
  icon = 'chevron',
  customIcon,
  theme,
  accentColor,
  borderColor,
  useDefaultColors = false,
  onClick,
  className = '',
}: CardCTAProps) {
  const resolvedAccent = theme?.Accent_Color ?? accentColor;
  const resolvedBorder = theme?.Border_Color ?? borderColor;

  const IconComponent = icon === 'external' ? ExternalLink : ChevronRight;
  const iconEl = customIcon ?? (
    <IconComponent
      className={`h-3.5 w-3.5 ${useDefaultColors ? 'text-[var(--brand-secondary)]' : ''}`}
    />
  );

  const colorStyle: React.CSSProperties = {
    color: resolvedAccent ?? undefined,
    borderColor: resolvedBorder ?? undefined,
  };

  const defaultColorClass = useDefaultColors ? 'text-[var(--brand-secondary)]' : '';
  const borderClass = useDefaultColors ? 'border-border' : '';

  const inner = (
    <>
      <span className={defaultColorClass}>{label}</span>
      {iconEl}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`flex w-full items-center justify-center gap-1 border-t py-3 text-xs font-semibold tracking-wide uppercase transition-colors ${borderClass} ${className}`}
        style={colorStyle}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      className={`flex items-center justify-center gap-1 border-t py-3 text-xs font-semibold tracking-wide uppercase ${borderClass} ${className}`}
      style={colorStyle}
    >
      {inner}
    </div>
  );
}
